import { UserAddOutlined } from "@ant-design/icons"
import {
  Button,
  CheckboxChangeEvent,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Tooltip,
  Typography,
} from "antd"
import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useState } from "react"

import { classAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import CheckboxWrapper from "#blocks/Form/CheckboxWrapper"
import { SFormHeader } from "#blocks/Form/formComponents"
import SessionSelect from "#blocks/Form/SessionSelect"
import TextOverrun from "#blocks/TextOverrun"
import { NULL_ID } from "#classes/Firebase"
import { CLASS_GROUPS } from "#constants/classOptions"
import {
  TRYOUT_FEES_GROUPED,
  TRYOUT_TUITION_FEES,
} from "#constants/tryoutOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { AddTryoutFormValues } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import AddStudentFormModal from "#forms/AddStudent/AddStudentFormModal"
import AnimatedReceiptSection from "#forms/AnimatedReceiptSection"
import { useOneDoc } from "#hooks/jotai/useSubscription"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import useOptimizedFilter from "#hooks/newHooks/useOptimizedFilter"
import { getSessionInfoFromId } from "#utils/classHelpers"
import { formatSemester } from "#utils/semesterHelpers"
import { setNestedFormField } from "#utils/setNestedFormField"
import {
  formatSkillLevelWithPending,
  getAllSkillLevelsWithPending,
} from "#utils/studentSkillHelpers"

import AddTryoutReceipt from "./AddTryoutReceipt"
import useTryoutFormData from "./useTryoutFormData"

const RECEIPT_ANIMATION_DURATION = 150

interface AddTryoutFormProps {
  initialValues: AddTryoutFormValues | undefined
  sendReceipt: boolean
  onSendReceiptChange: (value: boolean) => void
}

const AddTryoutForm = ({
  initialValues,
  sendReceipt,
  onSendReceiptChange,
}: AddTryoutFormProps) => {
  const isEdit = !!initialValues?.id

  const form = Form.useFormInstance<AddTryoutFormValues>()
  const sessionId = Form.useWatch("sessionId", form) ?? initialValues?.sessionId
  const isTryoutFeePaid =
    Form.useWatch(["step0", "isTryoutFeePaid"], form) ??
    initialValues?.step0?.isTryoutFeePaid
  const tryoutFeeId =
    Form.useWatch(["step0", "tryoutFeeId"], form) ??
    initialValues?.step0?.tryoutFeeId
  const tryoutFilterDate =
    Form.useWatch("tryoutFilterDate", form) ?? initialValues?.tryoutFilterDate

  const [showReceipt, setShowReceipt] = useState(true)
  const [showReceiptContent, setShowReceiptContent] = useState(true)

  // "New student" shortcut — only for roles allowed to create students.
  const canCreateStudent = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.CREATE
  )
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false)
  const handleStudentSaved = useCallback(
    (newStudentId: Uuid) => {
      // Select explicitly rather than waiting for the students subscription
      // to surface the new doc; the option label fills in once it arrives.
      form.setFieldValue("studentId", newStudentId)
    },
    [form]
  )

  const { student, studentOptions, semester, tryoutOptions } =
    useTryoutFormData(initialValues)

  const [classId] = sessionId ? getSessionInfoFromId(sessionId) : []
  const classObj = useOneDoc<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classId ?? NULL_ID
  )

  const classGroup = classObj?.group ?? ""

  useEffect(() => {
    const defaultTryoutFeeId =
      classGroup === CLASS_GROUPS.PORTFOLIO
        ? TRYOUT_TUITION_FEES[1].id
        : TRYOUT_TUITION_FEES[0].id
    setNestedFormField(form, ["step0", "tryoutFeeId"], defaultTryoutFeeId)
  }, [classGroup, form])

  const { filteredItems: filteredStudentOptions, onSearch } =
    useOptimizedFilter(studentOptions)

  // useOptimizedFilter caps the list at 20 fuzzy matches, so a student we
  // just created (or otherwise selected outside the current search) may not
  // be in it — AntD would then render the bare id. Pin the selection in.
  const selectedStudentId = Form.useWatch("studentId", form)
  const studentSelectOptions = useMemo(() => {
    if (!selectedStudentId) return filteredStudentOptions
    if (filteredStudentOptions.some((o) => o.value === selectedStudentId)) {
      return filteredStudentOptions
    }
    const selected = studentOptions.find((o) => o.value === selectedStudentId)
    return selected
      ? [selected, ...filteredStudentOptions]
      : filteredStudentOptions
  }, [filteredStudentOptions, studentOptions, selectedStudentId])

  // Update semester when tryout filter date changes
  useEffect(() => {
    form.setFieldsValue({ semester })
  }, [tryoutFilterDate, semester, form])

  // Keep selected session if valid; otherwise pick first non-disabled option
  useEffect(() => {
    if (!tryoutOptions.length) return

    const currentOption = tryoutOptions.find(
      (option) => option.value === sessionId
    )
    const hasValidCurrentSelection = !!currentOption && !currentOption.disabled
    if (hasValidCurrentSelection) return

    const firstEnabledOption = tryoutOptions.find(
      (option) => !option.disabled && option.value
    )
    if (!firstEnabledOption?.value) return

    form.setFieldValue("sessionId", firstEnabledOption.value)
  }, [sessionId, tryoutOptions, form])

  const handleShowReceiptChange = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked
    onSendReceiptChange(isChecked)

    if (isChecked) {
      setShowReceipt(true)
      setTimeout(() => {
        setShowReceiptContent(true)
      }, RECEIPT_ANIMATION_DURATION)
    } else {
      setShowReceiptContent(false)
      setTimeout(() => {
        setShowReceipt(false)
      }, RECEIPT_ANIMATION_DURATION)
    }
  }

  const skillLevels = Object.values(getAllSkillLevelsWithPending(student))
    .map(formatSkillLevelWithPending)
    .filter(Boolean)
    .join(", ")

  return (
    <>
      <Row gutter={40}>
        <Col span={16}>
          <Flex>
            <SFormHeader>Student Info</SFormHeader>
          </Flex>
          <Row gutter={16}>
            <Col span={isEdit ? 8 : 7}>
              <Form.Item
                name="studentId"
                label="Student"
                rules={[
                  {
                    required: !isEdit,
                    message: "Please select a student!",
                  },
                ]}
              >
                {isEdit ? (
                  <span>
                    {student?.firstName} {student?.lastName}
                  </span>
                ) : (
                  <Select
                    showSearch
                    filterOption={() => true}
                    options={studentSelectOptions}
                    onSearch={onSearch}
                    placeholder="Search for a student"
                  />
                )}
              </Form.Item>
            </Col>
            {!isEdit && canCreateStudent && (
              <Col span={4}>
                {/* Empty label keeps the button on the input baseline */}
                <Form.Item label=" " colon={false}>
                  <Tooltip title="Create a new student and select them for this tryout">
                    <Button
                      icon={<UserAddOutlined />}
                      onClick={() => setIsAddStudentOpen(true)}
                      aria-label="New student"
                      data-testid="tryout-new-student-button"
                    >
                      New student
                    </Button>
                  </Tooltip>
                </Form.Item>
                {isAddStudentOpen && (
                  <AddStudentFormModal
                    studentId={undefined}
                    onClose={() => setIsAddStudentOpen(false)}
                    onSaved={handleStudentSaved}
                  />
                )}
              </Col>
            )}
            {student && (
              <>
                <Col span={4}>
                  <Form.Item label="Skill levels">{skillLevels}</Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Personal phone">
                    <TextOverrun text={student?.personalPhone} />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item label="Personal email">
                    <TextOverrun text={student?.personalEmail} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>

          <SFormHeader>Class Info</SFormHeader>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Find sessions after (up to 2 weeks)"
                name="tryoutFilterDate"
              >
                <DatePicker
                  format="M/D/YY - dddd"
                  style={{ width: "100%" }}
                  minDate={dayjs("2016-01-01")}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Semester">
                <span>{formatSemester(semester)}</span>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={19}>
              <Form.Item
                name="sessionId"
                label="Tryout session"
                rules={[
                  {
                    required: true,
                    message: "Please select a tryout session!",
                  },
                ]}
              >
                <SessionSelect
                  placeholder="Select a tryout session"
                  options={tryoutOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <SFormHeader>Tryout fee</SFormHeader>
          <Row>
            <Col span={24}>
              <Flex col gap={10}>
                <Typography.Text strong>
                  {`Can pay with Zelle, Cash, Check, Credit card (don't charge
                  extra for over $50)`}
                </Typography.Text>
                <Flex gap={40}>
                  <CheckboxWrapper
                    name={["step0", "isTryoutFeePaid"]}
                    label="Tryout fee paid"
                  />
                  <CheckboxWrapper
                    // name="" // Intentionally blank to use the value prop
                    label="Send receipt"
                    value={sendReceipt}
                    onChange={handleShowReceiptChange}
                  />
                </Flex>
                <Flex>
                  <Form.Item name={["step0", "tryoutFeeId"]}>
                    <Select
                      options={TRYOUT_FEES_GROUPED}
                      style={{ width: 600 }}
                      placeholder="Select a tryout fee"
                    />
                  </Form.Item>
                </Flex>
              </Flex>
            </Col>
          </Row>
        </Col>

        {/* Receipt */}
        {showReceipt && (
          <Col span={8}>
            {showReceiptContent && (
              <AnimatedReceiptSection width={354} isOpen={showReceiptContent}>
                <AddTryoutReceipt
                  student={student}
                  tryoutFeeId={tryoutFeeId}
                  isPaid={isTryoutFeePaid}
                  sessionId={sessionId}
                />
              </AnimatedReceiptSection>
            )}
          </Col>
        )}
      </Row>

      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="semester" hidden>
        <Input />
      </Form.Item>
    </>
  )
}

export default AddTryoutForm
